import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CodeViewer from "@/components/CodeViewer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GitFork, Save, Upload, Edit } from "lucide-react";

export default function Clone() {
  const nav = useNavigate();
  const [source, setSource] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { config, regenerated_code }
  const [name, setName] = useState("Cloned Strategy");

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500000) { toast.error("File too large (max 500KB)"); return; }
    const text = await f.text();
    setSource(text);
    setName(f.name.replace(/\.[^.]+$/, "") + "_clone");
  };

  const analyze = async () => {
    if (!source.trim()) { toast.error("Paste or upload an .mq4 file first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/generate/clone", { mql4_code: source, model });
      setResult(data);
      if (data.config?.name) setName(data.config.name);
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setBusy(false); }
  };

  const openInBuilder = async () => {
    if (!result) return;
    try {
      const { data } = await api.post("/strategies", {
        name,
        source: "cloned",
        config: result.config || {},
        mql4_code: result.regenerated_code || source,
      });
      toast.success("Cloned strategy saved");
      nav(`/builder/${data.strategy_id}`);
    } catch { toast.error("Save failed"); }
  };

  const saveOnly = async () => {
    if (!result) return;
    try {
      await api.post("/strategies", {
        name,
        source: "cloned",
        config: result.config || {},
        mql4_code: result.regenerated_code || source,
      });
      toast.success("Cloned strategy saved");
      nav(`/dashboard`);
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="chip mb-3"><GitFork size={12}/> REVERSE ENGINEER</div>
          <h1 className="font-display text-4xl text-white tracking-tight">Clone any MQL4 robot.</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
            Upload or paste an existing <span className="font-mono text-white">.mq4</span> source. We'll extract its strategy into a
            reusable config so you can remix it in the visual builder.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-5 space-y-5">
            <div className="card-sharp p-5">
              <label className="label-tiny">Upload .mq4 or paste source</label>
              <label className="mt-2 flex items-center justify-center gap-2 border border-dashed border-white/15 rounded p-6 cursor-pointer hover:border-[var(--accent)]/60 transition-colors"
                data-testid="clone-dropzone">
                <Upload size={16} className="text-[var(--accent)]"/>
                <span className="font-mono text-xs text-[var(--text-secondary)]">Click to upload .mq4 / .txt</span>
                <input data-testid="clone-file-input" type="file" accept=".mq4,.mqh,.mq5,.txt" onChange={onFile} className="hidden"/>
              </label>
              <textarea data-testid="clone-source-input" value={source} onChange={(e)=>setSource(e.target.value)}
                rows={10} className="input-dark mt-3 !text-[11.5px]"
                placeholder="//+---- paste MQL4 source here ----+//"/>
              <label className="label-tiny mt-5">Analyzer model</label>
              <select data-testid="clone-model-select" value={model} onChange={(e)=>setModel(e.target.value)} className="input-dark">
                <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
                <option value="gpt-5.2">GPT-5.2</option>
                <option value="gemini-3-flash-preview">Gemini 3 Flash</option>
              </select>
              <button data-testid="clone-analyze-btn" onClick={analyze} disabled={busy}
                className="btn-primary w-full mt-5 flex items-center justify-center gap-2">
                <GitFork size={14}/> {busy ? "Analyzing…" : "Analyze & clone"}
              </button>
            </div>
          </section>

          <section className="lg:col-span-7">
            <div className="card-sharp overflow-hidden min-h-[520px] flex flex-col">
              {busy ? (
                <div className="flex items-center justify-center h-full py-24">
                  <div className="text-center">
                    <div className="h-12 w-12 rounded-full border-2 border-white/10 border-t-[var(--accent)] animate-spin mx-auto"></div>
                    <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] mt-4">
                      Extracting strategy…
                    </p>
                  </div>
                </div>
              ) : result ? (
                <div className="flex flex-col h-full">
                  <div className="p-5 border-b border-white/5" data-testid="clone-result-summary">
                    <div className="flex items-center gap-2 mb-3">
                      <input data-testid="clone-name-input" value={name} onChange={(e)=>setName(e.target.value)} className="input-dark !max-w-[280px]"/>
                      <button data-testid="clone-open-builder-btn" onClick={openInBuilder} className="btn-primary flex items-center gap-2 text-sm">
                        <Edit size={13}/> Open in builder
                      </button>
                      <button data-testid="clone-save-btn" onClick={saveOnly} className="btn-outline flex items-center gap-2 text-sm">
                        <Save size={13}/> Save
                      </button>
                    </div>
                    {result.config?.summary && (
                      <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                        <span className="font-mono uppercase tracking-widest text-[var(--text-muted)]">Summary · </span>
                        {result.config.summary}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="chip">{result.config?.symbol || "—"}</span>
                      <span className="chip">{(result.config?.timeframe||"PERIOD_H1").replace("PERIOD_","")}</span>
                      <span className="chip chip-profit">{result.config?.entry_rules?.length || 0} entry</span>
                      <span className="chip chip-loss">{result.config?.exit_rules?.length || 0} exit</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <CodeViewer code={result.regenerated_code || source} filename={`${(name||"clone").replace(/[^A-Za-z0-9_]+/g,"_")}.mq4`}/>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full py-24" data-testid="clone-empty-state">
                  <div className="text-center max-w-md">
                    <div className="h-12 w-12 rounded-sm border border-white/10 mx-auto flex items-center justify-center">
                      <GitFork size={20} className="text-[var(--accent)]"/>
                    </div>
                    <h3 className="font-display text-xl text-white mt-4">Waiting for source</h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-2">
                      Upload an .mq4 file or paste source. The AI will map it back into an editable strategy.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
